import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ProductCard from "../product/ProductCard";
import SkeletonGrid from "../common/SkeletonGrid";
import ShopHero from "./ShopHero";
import ShopReviews from "./ShopReviews";
import { useAuth } from "../../contexts/AuthContext";
import { useSellerAuth } from "../../contexts/SellerAuthContext";
import { fetchShop, fetchShopProducts } from "../../api/shops";
import { shopPath } from "../../utils/shopPath";
import { useSEO } from "../../utils/seo";
import "./ShopPage.css";
import "./ShopHero.css";

/* A seller's public shop: /shop/<handle> (owner, 2026-10-17).

   Who the seller is, why they can be trusted, and everything they sell.
   Customers never contact sellers on Emporia, so there is no message
   button and no contact detail anywhere; "Report this shop" goes to
   Emporia's own staff. Every field shown comes from the public API
   (backend app/shop_public.py), which never carries the legal name.

   Simplified 2026-10-19 (owner): the shop card, a centred search, then the
   products. No tabs, no sort, no category filters. "What Our Customers Say"
   (ShopReviews.jsx) sits inside the product grid after its third visual
   row (owner's brief, 2026-10-19). */

const SEARCH_DELAY_MS = 300;
export const REVIEWS_AFTER_ROWS = 3;

/** Where the reviews go among the products: after row 3 at the grid's
    current column count, or after the last product when there are fewer. */
export function reviewsIndex(columns, productCount) {
  return Math.min(Math.max(1, columns) * REVIEWS_AFTER_ROWS, productCount);
}

// The grid's live column count (repeat(auto-fill, ...) changes it with the
// width), read from the browser's own layout, so "row 3" is a visual row.
function useGridColumns(ref, ready) {
  const [columns, setColumns] = useState(5);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return undefined;
    const read = () => {
      const tracks = String(window.getComputedStyle(el).gridTemplateColumns || "");
      const n = tracks === "none" ? 0 : tracks.split(/\s+/).filter(Boolean).length;
      if (n > 0) setColumns(n);
    };
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, ready]);
  return columns;
}

function ShopProducts({ shop, onAddToCart, reviews }) {
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");                 // the query actually sent, after the pause
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const sentinel = useRef(null);
  const requestId = useRef(0);
  const grid = useRef(null);
  const columns = useGridColumns(grid, Boolean(items && items.length));

  useEffect(() => {
    const t = setTimeout(() => setQ(query), SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const id = ++requestId.current;
    setItems(null);
    setError("");
    fetchShopProducts(shop.handle, { q, sort: "newest" })
      .then((page) => {
        if (id !== requestId.current) return;
        setItems(page.items || []);
        setCursor(page.next_cursor || null);
      })
      .catch((err) => {
        if (id !== requestId.current) return;
        setItems([]);
        setError(err.status === 429 ? "Please wait a moment and try again." : "We couldn't load this shop's products. Please try again.");
      });
  }, [shop.handle, q]);

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    const id = requestId.current;
    setLoadingMore(true);
    fetchShopProducts(shop.handle, { q, sort: "newest", cursor })
      .then((page) => {
        if (id !== requestId.current) return;
        setItems((prev) => [...(prev || []), ...(page.items || [])]);
        setCursor(page.next_cursor || null);
      })
      .catch((err) => {
        if (id !== requestId.current) return;
        setError(err.status === 429 ? "Please wait a moment and try again." : "We couldn't load more products.");
      })
      .finally(() => setLoadingMore(false));
  }, [cursor, loadingMore, shop.handle, q]);

  // Infinite scroll, with the "Load more" button as the fallback (and for
  // anyone who would rather press it).
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !cursor || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMore();
    }, { rootMargin: "400px 0px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [cursor, loadMore]);

  const filtered = Boolean(q.trim());
  const at = items ? reviewsIndex(columns, items.length) : 0;
  const card = (p, i) => (
    <div key={p.id} className="product-card-anim" style={{ "--i": i % 20 }}>
      <ProductCard product={p} onAddToCart={onAddToCart} showSeller={false} />
    </div>
  );

  return (
    <section className="shop-products" aria-label={`${shop.display_name}'s products`}>
      <div className="shop-controls">
        <input
          type="search"
          className="shop-search"
          placeholder={`Search ${shop.display_name}`}
          aria-label={`Search ${shop.display_name}'s products`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <p className="shop-error" role="alert">{error}</p>}
      {items === null ? (
        <SkeletonGrid count={8} className="shop-grid" />
      ) : items.length === 0 ? (
        !error && (
          <div className="shop-empty">
            {filtered
              ? <p>No products in this shop match <strong>"{q.trim()}"</strong>.</p>
              : <p>This shop has no products on sale right now.</p>}
          </div>
        )
      ) : null}
      {items !== null && items.length === 0 && reviews}
      {items === null || items.length === 0 ? null : (
        <>
          <div className="shop-grid" ref={grid}>
            {items.slice(0, at).map(card)}
            {reviews}
            {items.slice(at).map((p, i) => card(p, at + i))}
          </div>
          {cursor && (
            <div className="shop-more" ref={sentinel}>
              <button type="button" className="shop-btn shop-btn--ghost" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ShopMessage({ title, children }) {
  return (
    <main className="shop-page shop-page--message">
      <div className="shop-message">
        <h1>{title}</h1>
        {children}
        <p><Link to="/search?tab=sellers">Find another shop</Link> or <Link to="/">go to the homepage</Link>.</p>
      </div>
    </main>
  );
}

const ShopPage = ({ onAddToCart }) => {
  const { handle = "" } = useParams();
  const navigate = useNavigate();
  const { token: userToken } = useAuth() || {};
  const { token: sellerToken } = useSellerAuth() || {};
  const [result, setResult] = useState(null);   // { shop } | { notFound } | { unavailable } | { failed }

  useEffect(() => {
    let live = true;
    setResult(null);
    fetchShop(handle, { userToken, sellerToken })
      .then((r) => {
        if (!live) return;
        // An old handle answers 301 and fetch follows it: put the current
        // handle in the address bar, without a second history entry.
        if (r.shop && r.shop.handle && r.shop.handle !== handle.toLowerCase()) {
          navigate(`${shopPath(r.shop.handle)}${window.location.search}`, { replace: true });
        }
        setResult(r);
      })
      .catch(() => live && setResult({ failed: true }));
    return () => { live = false; };
  }, [handle, navigate, userToken, sellerToken]);

  const shop = result?.shop;
  useSEO({
    title: shop ? `${shop.display_name} – Shop on Emporia` : result?.notFound ? "Shop not found" : result?.unavailable ? "Shop unavailable" : "Shop",
    description: shop
      ? (shop.description || `Shop ${shop.display_name} on Emporia, a Kenyan online marketplace. Pay securely with M-Pesa through Emporia's checkout.`).slice(0, 160)
      : "Shops on Emporia, a Kenyan online marketplace.",
    path: shop ? shopPath(shop.handle) : shopPath(handle),
    noindex: Boolean(!shop || shop.live_product_count === 0),
  });

  if (result === null) {
    return (
      <main className="shop-page" aria-busy="true">
        <div className="shop-hero shop-hero--loading" aria-hidden="true" />
        <SkeletonGrid count={8} className="shop-grid" />
      </main>
    );
  }
  if (result.notFound) {
    return <ShopMessage title="Shop not found"><p>There is no shop at this address.</p></ShopMessage>;
  }
  if (result.unavailable) {
    return <ShopMessage title="This shop isn't available"><p>This shop is not selling on Emporia at the moment.</p></ShopMessage>;
  }
  if (result.failed) {
    return <ShopMessage title="Something went wrong"><p>We couldn't load this shop. Please try again shortly.</p></ShopMessage>;
  }
  return (
    <main className="shop-page">
      <ShopHero shop={shop} />
      <ShopProducts shop={shop} onAddToCart={onAddToCart} reviews={<ShopReviews shop={shop} />} />
    </main>
  );
};

export default ShopPage;
